import * as React from 'react'
import {consoleFancyClient, extractData} from '@frontrow/console/src/open-api/clients'
import {H1} from '@frontrow/console/src/components/typography'
import {CenteredSpinner} from '@frontrow/console/src/components/spinner'
import {
  csvFileErrorsFromError,
  type SyncResultT,
  type IsDryRun,
  type RosteringFilesT
} from '@frontrow/console/src/entities/models/roster-sync'
import {type CsvFileErrorT} from '@frontrow/console/src/entities/models/roster-sync'

import {type ImportFileType} from '@frontrow/console/src/components/pages/roster-page/import-row'
import RosterSyncCsvUpload from '@frontrow/console/src/components/pages/roster-page/roster-sync-page/csv-upload'
import CsvFileErrors from '@frontrow/console/src/components/common/csv-file-errors'
import RosterSyncResults from '@frontrow/console/src/components/pages/roster-page/roster-sync-page/sync-results'

type SyncStatusT =
  | {
      tag: 'pending'
    }
  | {
      tag: 'loading'
    }
  | {
      tag: 'validation-error'
      value: Array<CsvFileErrorT>
    }
  | {
      tag: 'dry-run'
      value: Array<SyncResultT>
    }
  | {
      tag: 'real-run'
      value: Array<SyncResultT>
    }

export default function RosterSyncPageContainer(): React.ReactElement {
  const [districtId, setDistrictId] = React.useState<number | null>(null)
  const [teachersFile, setTeachersFile] = React.useState<File | null>(null)
  const [studentsFile, setStudentsFile] = React.useState<File | null>(null)
  const [enrollmentsFile, setEnrollmentsFile] = React.useState<File | null>(null)
  const [coursesFile, setCoursesFile] = React.useState<File | null>(null)
  const [syncStatus, setSyncStatus] = React.useState<SyncStatusT>({
    tag: 'pending'
  })

  const handleSubmitSuccess = (isDryRun: IsDryRun, value: Array<SyncResultT>) => {
    setSyncStatus(isDryRun === 'dry-run' ? {tag: 'dry-run', value} : {tag: 'real-run', value})
  }

  const handleSubmitFailure = (err: unknown) => {
    setSyncStatus({
      tag: 'validation-error',
      value: csvFileErrorsFromError(err)
    })
  }

  const patchRosters = (
    districtId: number,
    isDryRun: IsDryRun,
    rosteringFiles: RosteringFilesT
  ): Promise<unknown> => {
    const {teachersCsv, studentsCsv, coursesCsv, enrollmentsCsv} = rosteringFiles

    const formData = new FormData()
    formData.append('teachers.csv', teachersCsv, teachersCsv.name)
    formData.append('students.csv', studentsCsv, studentsCsv.name)
    formData.append('enrollments.csv', enrollmentsCsv, enrollmentsCsv.name)
    formData.append('courses.csv', coursesCsv, coursesCsv.name)

    // The backend reads `dry-run` as a presence flag, so a real run must omit it.
    const query = isDryRun === 'dry-run' ? {'dry-run': true} : undefined

    // The spec types the multipart body as `unknown`.
    return consoleFancyClient
      .PATCH('/2/districts/{DistrictP-DistrictId}/rosters', {
        params: {path: {'DistrictP-DistrictId': districtId}, query},
        body: formData
      })
      .then(extractData)
      .then(
        // The spec's `failure`/`failedItems` fields are looser than SyncResultT's
        // (unknown vs. the app's stricter LogMessageT/FailedLogsT shapes).
        result => handleSubmitSuccess(isDryRun, result as SyncResultT[]),
        handleSubmitFailure
      )
  }

  const handleSubmit = async (isDryRun: IsDryRun): Promise<void> => {
    if (districtId !== null && teachersFile && studentsFile && coursesFile && enrollmentsFile) {
      setSyncStatus({tag: 'loading'})
      await patchRosters(districtId, isDryRun, {
        teachersCsv: teachersFile,
        studentsCsv: studentsFile,
        coursesCsv: coursesFile,
        enrollmentsCsv: enrollmentsFile
      })
    } else {
      throw new Error('Did not include districtId')
    }
  }

  const handleSubmitDryRun = () => handleSubmit('dry-run')
  const handleSubmitRealRun = () => handleSubmit('real-run')

  const handleImportFile = (file: File, fileType: ImportFileType) => {
    switch (fileType) {
      case 'teachers-csv':
        setTeachersFile(file)
        break
      case 'students-csv':
        setStudentsFile(file)
        break
      case 'enrollments-csv':
        setEnrollmentsFile(file)
        break
      case 'courses-csv':
        setCoursesFile(file)
        break
      default:
        throw new Error('Unknown Import File Type')
    }
  }

  const renderCsvUpload = (): React.ReactElement => {
    return (
      <RosterSyncCsvUpload
        setDistrictId={setDistrictId}
        districtIdIsSet={districtId !== null}
        handleImportFile={handleImportFile}
        teachersCsvIsUploaded={!!teachersFile}
        studentsCsvIsUploaded={!!studentsFile}
        coursesCsvIsUploaded={!!coursesFile}
        enrollmentsCsvIsUploaded={!!enrollmentsFile}
        handleSubmit={handleSubmitDryRun}
      />
    )
  }

  switch (syncStatus.tag) {
    case 'pending':
      return (
        <React.Fragment>
          <H1>Roster Sync</H1>
          {renderCsvUpload()}
        </React.Fragment>
      )
    case 'loading':
      return <CenteredSpinner />
    case 'validation-error':
      return (
        <React.Fragment>
          <H1>Roster Sync (Validation Error)</H1>
          {renderCsvUpload()}
          <CsvFileErrors errors={syncStatus.value} />
        </React.Fragment>
      )
    case 'dry-run':
      return (
        <React.Fragment>
          <H1>Roster Sync (Dry Run)</H1>
          {renderCsvUpload()}
          <RosterSyncResults results={syncStatus.value} handleSubmit={handleSubmitRealRun} />
        </React.Fragment>
      )
    case 'real-run':
      return (
        <React.Fragment>
          <H1>Roster Sync (Completed)</H1>
          {renderCsvUpload()}
          <RosterSyncResults results={syncStatus.value} />
        </React.Fragment>
      )
    default:
      return <H1>Something went terribly wrong</H1>
  }
}
